import { useCallback } from 'react';
import { type EditorView } from '@tiptap/pm/view';

import { logger } from '@zep/core-logger';

import { prepareTiptapHTML } from '@/shared/features/rich-text';
import { countFormulaSymbols } from '@/shared/features/rich-text/utils/countFormulaSymbols';
import { extractBareText } from '@/shared/utils/extractBareText';
import { QUESTION_TEXT_MAX_LENGTH } from '@/views/build/form/schema/quiz/QuizBaseSchema';

import {
  goToNextFocusable,
  goToPreviousFocusable,
} from '../../focus/focusChain';
import {
  isNextFocusKeyboardEvent,
  isPreviousFocusKeyboardEvent,
} from '../../focus/focusUtils';
import { useQuizList } from '../../useQuizList';
import { useBuildTextEditor } from '../hook/useBuildTextEditor';
import { useQuizFormField } from '../hook/useQuizFormField';

export const useQuestionFieldTextEditor = ({
  quizIndex,
}: {
  quizIndex: number;
}) => {
  const { quizList, handleCreate } = useQuizList();
  const [question, updateQuestion] = useQuizFormField({
    field: 'question',
  });

  const [type] = useQuizFormField({ field: 'type' });

  const [questionRichText, updateQuestionRichText] = useQuizFormField({
    field: 'questionRichText',
  });

  const handleKeyDown = useCallback(
    (_: EditorView, event: KeyboardEvent) => {
      //새로운 퀴즈를 만들지를 먼저 결정
      if (
        type === 'ox' &&
        quizList.length === quizIndex + 1 &&
        (event.key === 'Enter' || event.key === 'Tab') &&
        !event.shiftKey
      ) {
        event.preventDefault();
        handleCreate('ox');
        logger.track('add_quizItem', {
          type: 'ox',
          position: 'shortcut_key',
        });
        return true;
      }

      if (isNextFocusKeyboardEvent(event)) {
        event.preventDefault();
        goToNextFocusable({
          current: `quizList.${quizIndex}.question`,
        });
        return true;
      }

      if (isPreviousFocusKeyboardEvent(event)) {
        event.preventDefault();
        goToPreviousFocusable({
          current: `quizList.${quizIndex}.question`,
        });
        return true;
      }
    },
    [quizIndex, type, quizList.length, handleCreate],
  );

  const { editor } = useBuildTextEditor({
    content: questionRichText || (question ? prepareTiptapHTML(question) : ''),
    onUpdate: value => {
      updateQuestion(extractBareText(value));
      updateQuestionRichText(value);
    },
    characterLimit: QUESTION_TEXT_MAX_LENGTH,
    blockSeparator: '\n',
    formulaCounter: countFormulaSymbols,
  });

  editor?.setOptions({
    editorProps: {
      handleKeyDown,
    },
  });

  return { editor };
};
